// Profile › Devices (design v11, ADR 0018): the signed-in user's sessions, online only. The server names each
// session's app from the `X-TaskCool-Client` hint (sent by api()) or, without it, from the User-Agent.
import { TransportError } from '@/app/ports';
import { api, ensureSessionCsrf } from './client';

export interface DeviceSession {
  id: string;
  app: 'web' | 'ios' | 'android' | 'macos' | 'windows' | 'other';
  /** device model / operating system, when known */
  device: string | null;
  browser: string | null;
  ip: string | null;
  /** ISO 8601 UTC */
  last_seen_at: string | null;
  created_at: string | null;
  /** this browser's own session (sign out instead of revoking it) */
  current: boolean;
}

/** Authenticated `/auth/*` write: session CSRF first, one retry after a stale token. */
async function authWrite(method: 'POST' | 'DELETE', path: string): Promise<void> {
  await ensureSessionCsrf();
  try { await api<void>(method, path); }
  catch (e) {
    if (!(e instanceof TransportError) || e.code !== 'csrf_failed') throw e;
    await ensureSessionCsrf(true);
    await api<void>(method, path);
  }
}

/** GET /auth/sessions → the caller's sessions (current one flagged). */
export async function listSessions(): Promise<DeviceSession[]> {
  const r = await api<{ sessions?: DeviceSession[] }>('GET', '/auth/sessions');
  return Array.isArray(r?.sessions) ? r.sessions : [];
}

/** DELETE /auth/sessions/{id}: signs that device out. The current session answers 409 (`conflict`) — use sign-out. */
export async function revokeSession(id: string): Promise<void> {
  await authWrite('DELETE', '/auth/sessions/' + encodeURIComponent(id));
}

/** POST /auth/sessions/revoke-others: signs out every other device of this account. */
export async function revokeOtherSessions(): Promise<void> {
  await authWrite('POST', '/auth/sessions/revoke-others');
}
